// Shared "Mark as lost — reason" prompt. Used from the lead drawer, the leads
// list inline status pill, and the bulk action bar so that every path enforces
// the same rule: marking a lead lost requires a reason, which the activity
// timeline then surfaces.

import React, { useEffect, useRef, useState } from "react";
import { Loader2, TrendingDown, X } from "lucide-react";
import Modal from "../overlays/Modal";
import { Button } from "../atoms";

export default function LostReasonModal({
  open,
  onClose,
  onSubmit,           // (reason: string) => Promise<void> | void
  isPending = false,
  // Subject line: pass either a single lead title or a count for bulk.
  count = 1,
  title,              // optional override
}) {
  const [reason, setReason] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setReason("");
      // Autofocus the textarea once the modal animates in.
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [open]);

  const trimmed = reason.trim();
  const canSubmit = trimmed.length > 0 && !isPending;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit?.(trimmed);
  };

  const subject = title
    ? title
    : count > 1
      ? `Mark ${count} leads as lost`
      : "Mark lead as lost";

  return (
    <Modal open={open} onClose={isPending ? () => {} : onClose} width={480}>
      <div style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: "var(--crm-danger-soft)", color: "var(--crm-danger)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <TrendingDown size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)" }}>
              {subject}
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.45 }}>
              Tell the team why this deal didn't close. The reason shows up in the lead's activity timeline so others can learn from it.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            aria-label="Close"
            style={{
              border: "none", background: "transparent", cursor: isPending ? "not-allowed" : "pointer",
              color: "var(--color-text-secondary)", padding: 4,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 6 }}>
          Lost reason <span style={{ color: "var(--crm-danger)" }}>*</span>
        </label>
        <textarea
          ref={inputRef}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={isPending}
          placeholder="e.g. Lost to competitor on price — they offered 20% lower and net-60 terms we couldn't match."
          rows={4}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid var(--color-border-subtle)",
            background: "var(--color-bg-surface)",
            color: "var(--color-text-primary)",
            fontSize: 13,
            outline: "none",
            resize: "vertical",
            fontFamily: "inherit",
            lineHeight: 1.5,
          }}
        />
        <div style={{ marginTop: 4, fontSize: 11, color: "var(--color-text-secondary)" }}>
          Tip: ⌘+Enter to submit
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={submit}
            disabled={!canSubmit}
            leftIcon={isPending ? <Loader2 size={13} className="animate-spin" /> : <TrendingDown size={13} />}
          >
            {count > 1 ? `Mark ${count} lost` : "Mark lost"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
